import { ArrowLeft, CopyPlus, Loader2, Plus, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ErrorBoundary } from "src/components/ErrorBoundary";
import { ComposerAddCardButton } from "src/components/HappeningNow/compose/ComposerAddCardButton";
import { ComposerCardEditor } from "src/components/HappeningNow/compose/ComposerCardEditor";
import { ComposerCarousel } from "src/components/HappeningNow/compose/ComposerCarousel";
import { ComposerDefaultsBar } from "src/components/HappeningNow/compose/ComposerDefaultsBar";
import { HappeningNowLocked } from "src/components/HappeningNow/HappeningNowLocked";
import { PublishCarouselButton } from "src/components/HappeningNow/PublishCarouselButton";
import { ConfirmDialog } from "src/components/ui/ConfirmDialog";
import { InlineAlert } from "src/components/ui/InlineAlert";
import { SlabButton } from "src/components/ui/SlabButton";
import { Constants } from "src/constant";
import { actorFromAuth } from "src/lib/activityService";
import {
  computePublishStatus,
  liveCardsRemovedOnPublish,
} from "src/lib/happeningNowPanelLogic";
import { hasPermission, Permission } from "src/lib/roles";
import { selectUser, selectUserProfile } from "src/store/authSlice";
import { useCarouselPublicationsStore } from "src/store/carouselPublicationsStore";
import type { ComposerCard } from "src/store/happeningNowComposerStore";
import {
  cardReadiness,
  useHappeningNowComposerStore,
} from "src/store/happeningNowComposerStore";
import { useHappeningNowPanelStore } from "src/store/happeningNowPanelStore";
import { useShallow } from "zustand/react/shallow";

/**
 * Carousel-first composer (`/happening-now/compose`), the only Happening Now
 * composer. Build many cards from a batch of images, edit each via a slidable
 * carousel, then commit the ready ones to the Firestore draft this page
 * publishes. The classic `/happening-now/create` composer has been retired;
 * that path now redirects here.
 */
const HappeningNowComposeCarouselPanel: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const userProfile = useSelector(selectUserProfile);
  const actor = actorFromAuth(user, userProfile);

  const {
    defaults,
    cards,
    selectedIndex,
    committing,
    composerError,
    composerSuccess,
    initDefaults,
    patchDefaults,
    loadDraft,
    addImages,
    selectIndex,
    moveCard,
    patchSelected,
    removeStaged,
    deleteCommitted,
    clearComposer,
    clearFeedback,
    saveToDraft,
  } = useHappeningNowComposerStore(
    useShallow((s) => ({
      defaults: s.defaults,
      cards: s.cards,
      selectedIndex: s.selectedIndex,
      committing: s.committing,
      composerError: s.error,
      composerSuccess: s.success,
      initDefaults: s.initDefaults,
      patchDefaults: s.patchDefaults,
      loadDraft: s.loadDraft,
      addImages: s.addImages,
      selectIndex: s.selectIndex,
      moveCard: s.moveCard,
      patchSelected: s.patchSelected,
      removeStaged: s.removeStaged,
      deleteCommitted: s.deleteCommitted,
      clearComposer: s.clearComposer,
      clearFeedback: s.clearFeedback,
      saveToDraft: s.saveToDraft,
    })),
  );

  const {
    items,
    liveItems,
    load,
    publishing,
    publishError,
    publishCarousel,
    seedFromLive,
    seeding,
  } = useHappeningNowPanelStore(
    useShallow((s) => ({
      items: s.items,
      liveItems: s.liveItems,
      load: s.load,
      publishing: s.publishing,
      publishError: s.error,
      publishCarousel: s.publishCarousel,
      seedFromLive: s.seedFromLive,
      seeding: s.seeding,
    })),
  );

  const { publications, load: loadPublications } = useCarouselPublicationsStore(
    useShallow((s) => ({ publications: s.publications, load: s.load })),
  );

  useEffect(() => {
    initDefaults();
    void loadDraft();
    void load();
    void loadPublications();
  }, [initDefaults, loadDraft, load, loadPublications]);

  const publishStatus = useMemo(
    () => computePublishStatus(items, liveItems),
    [items, liveItems],
  );
  const lastPublishedAtMs = publications[0]?.publishedAt.toMillis() ?? null;

  // Cards that would be written on Save: ready new cards + ready edited cards.
  const pendingCount = useMemo(
    () =>
      cards.filter((c) => {
        if (!cardReadiness(c).ready) return false;
        return c.committedId === null || c.dirty;
      }).length,
    [cards],
  );
  // Committed cards edited into an invalid state: not savable (excluded from
  // pendingCount), and Publish would ship the server's old version and silently
  // drop the edit — so they must gate Publish too.
  const brokenEdits = useMemo(
    () =>
      cards.filter(
        (c) => c.committedId !== null && c.dirty && !cardReadiness(c).ready,
      ).length,
    [cards],
  );
  // Publish must reflect the saved draft: block on any unsaved change, whether
  // it's savable now (pendingCount) or a broken edit that can't be saved yet.
  const publishBlocked = pendingCount > 0 || brokenEdits > 0;
  // Cards the operator still has to fix before they can be saved — new cards
  // plus committed cards edited into an invalid state.
  const needsDetails = useMemo(
    () =>
      cards.filter(
        (c) => !cardReadiness(c).ready && (c.committedId === null || c.dirty),
      ).length,
    [cards],
  );

  const creatorDisplayName =
    userProfile?.displayName?.trim() ||
    user?.displayName?.trim() ||
    user?.email ||
    user?.uid ||
    "";

  const handleSave = useCallback(async () => {
    if (!user?.uid) return;
    const { created, updated } = await saveToDraft(
      user.uid,
      creatorDisplayName,
      actor,
    );
    // Refresh the publish bar (draft/live diff) after any write.
    if (created > 0 || updated > 0) await load();
  }, [user?.uid, creatorDisplayName, actor, saveToDraft, load]);

  const handleRemove = useCallback(
    (key: string) => {
      const card = cards.find((c: ComposerCard) => c.key === key);
      if (!card) return;
      if (card.committedId) {
        void deleteCommitted(card.committedId, actor).then((ok) => {
          if (ok) void load();
        });
      } else {
        removeStaged(key);
      }
    },
    [cards, deleteCommitted, removeStaged, actor, load],
  );

  const handleReorder = useCallback(
    (activeKey: string, overKey: string) => {
      // Persist the drag, then refresh the panel store so the publish bar's
      // in-sync/changed status reflects the new order.
      void moveCard(activeKey, overKey, actor).then(() => load());
    },
    [moveCard, actor, load],
  );

  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);
  const removedOnPublish = useMemo(
    () => liveCardsRemovedOnPublish(items, liveItems),
    [items, liveItems],
  );
  const handlePublish = useCallback(() => setConfirmPublishOpen(true), []);
  const doPublish = useCallback(async () => {
    if (!user?.uid) return;
    await publishCarousel(user.uid, actor, { skipConfirm: true });
    // Publish clears the draft, so refresh the composer too — otherwise the
    // carousel keeps rendering just-deleted docs and the next edit/delete/drag
    // throws "Item not found".
    await Promise.all([load(), loadDraft()]);
    setConfirmPublishOpen(false);
  }, [user?.uid, actor, publishCarousel, load, loadDraft]);

  const handlePullLive = useCallback(async () => {
    if (!user?.uid) return;
    await seedFromLive(user.uid, actor);
    // Pull the freshly-seeded draft docs into the carousel as committed cards.
    await loadDraft();
  }, [user?.uid, actor, seedFromLive, loadDraft]);

  // Clearing the draft is destructive, so confirm in a dialog first.
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const doClear = useCallback(async () => {
    await clearComposer(actor);
    await load();
    setConfirmClearOpen(false);
  }, [clearComposer, actor, load]);

  const hasCommitted = useMemo(
    () => cards.some((c) => c.committedId !== null),
    [cards],
  );

  if (
    !userProfile ||
    !hasPermission(userProfile.role, Permission.MANAGE_HAPPENING_NOW)
  ) {
    return <HappeningNowLocked />;
  }

  const selectedCard = cards[selectedIndex];

  return (
    <div
      className="mx-auto max-w-3xl pb-10 pt-2 sm:max-w-4xl sm:pt-4 lg:max-w-5xl"
      style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => navigate(Constants.HAPPENING_NOW_ROUTE)}
          className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-extrabold text-white/90 ring-2 ring-white/20 hover:bg-white/15"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Back to Happening
          now
        </button>
      </div>

      <header className="mb-6">
        <h1 className="text-2xl font-extrabold tracking-tight text-white drop-shadow-sm sm:text-3xl">
          Compose carousel
        </h1>
        <p className="text-sm font-bold text-white/80 sm:text-base">
          Drop a batch of images, tune each card, then add the ready ones to
          your draft.
        </p>
      </header>

      <ErrorBoundary label="the composer">
        <div className="flex flex-col gap-5">
          <div>
            <PublishCarouselButton
              status={publishStatus}
              lastPublishedAt={lastPublishedAtMs}
              publishing={publishing}
              onPublish={handlePublish}
              disabled={publishBlocked}
            />
            {publishBlocked && (
              <p className="mt-2 text-center text-xs font-bold text-[#FCD34D]">
                {brokenEdits > 0
                  ? `Fix ${brokenEdits} card${
                      brokenEdits === 1 ? "" : "s"
                    } with errors, then Save, to publish.`
                  : `Save your ${pendingCount} unsaved ${
                      pendingCount === 1 ? "change" : "changes"
                    } to publish ${pendingCount === 1 ? "it" : "them"}.`}
              </p>
            )}
          </div>

          {(composerError || publishError) && (
            <InlineAlert variant="error">
              {composerError || publishError}
            </InlineAlert>
          )}
          {composerSuccess && (
            <InlineAlert variant="success" onDismiss={clearFeedback}>
              {composerSuccess}
            </InlineAlert>
          )}

          <ComposerDefaultsBar defaults={defaults} onPatch={patchDefaults} />

          {cards.length === 0 ? (
            <div className="flex flex-col gap-3">
              <ComposerAddCardButton onAdd={addImages} />
              {liveItems.length > 0 && (
                <div className="flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-white/25 bg-white/5 p-4 text-center">
                  <p className="text-sm font-bold text-white/70">
                    Or start from what's already live.
                  </p>
                  <button
                    type="button"
                    onClick={() => void handlePullLive()}
                    disabled={seeding}
                    className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border-2 border-[#6366F1] bg-[#EEF2FF] px-4 text-sm font-extrabold uppercase text-[#4338CA] shadow-[0_3px_0_#C7D2FE] transition-all active:translate-y-0.5 disabled:opacity-50"
                  >
                    {seeding ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    ) : (
                      <CopyPlus className="h-4 w-4" aria-hidden />
                    )}
                    Pull {liveItems.length}{" "}
                    {liveItems.length === 1 ? "live card" : "live cards"} in
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <ComposerCarousel
                cards={cards}
                selectedIndex={selectedIndex}
                onSelect={selectIndex}
                onRemove={handleRemove}
                onReorder={handleReorder}
                onAdd={addImages}
              />

              {selectedCard && (
                <ComposerCardEditor
                  key={selectedCard.key}
                  card={selectedCard}
                  index={selectedIndex}
                  defaults={defaults}
                  onPatch={patchSelected}
                />
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <ComposerAddCardButton onAdd={addImages} compact />
                <div className="flex items-center gap-3 sm:ml-auto">
                  <SlabButton
                    type="button"
                    variant="primary"
                    onClick={() => void handleSave()}
                    disabled={committing || pendingCount === 0}
                    backgroundColor="#14B8A6"
                    lipColor="#0F766E"
                    shadowLiftPx={6}
                    shadowLiftActivePx={2}
                    className="px-6 text-base disabled:opacity-60"
                  >
                    {committing ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                        Saving…
                      </>
                    ) : (
                      <>
                        <Plus className="h-5 w-5" aria-hidden />
                        Save {pendingCount}{" "}
                        {pendingCount === 1 ? "card" : "cards"} to draft
                      </>
                    )}
                  </SlabButton>
                  <button
                    type="button"
                    onClick={() => setConfirmClearOpen(true)}
                    disabled={committing}
                    className="inline-flex items-center justify-center gap-1.5 rounded-xl border-2 border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2.5 text-xs font-extrabold uppercase text-[#B91C1C] shadow-[0_3px_0_#FCA5A5] transition-all active:translate-y-0.5 disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                    {hasCommitted ? "Clear draft" : "Discard new"}
                  </button>
                </div>
              </div>

              {needsDetails > 0 && (
                <p className="text-center text-xs font-bold text-white/70">
                  {needsDetails} card{needsDetails === 1 ? "" : "s"} still need
                  {needsDetails === 1 ? "s" : ""} details — slide through and
                  fill the highlighted fields.
                </p>
              )}
            </>
          )}
        </div>
      </ErrorBoundary>

      <ConfirmDialog
        open={confirmPublishOpen}
        title="Publish carousel?"
        message={
          <>
            <p>
              This pushes {items.length} {items.length === 1 ? "card" : "cards"}{" "}
              to the live carousel, visible in the app immediately.
            </p>
            {removedOnPublish.length > 0 && (
              <p className="mt-2 text-[#B91C1C]">
                This will replace all the existing cards in the live carousel.
              </p>
            )}
          </>
        }
        confirmLabel="Publish"
        destructive={removedOnPublish.length > 0}
        busy={publishing}
        onConfirm={() => void doPublish()}
        onCancel={() => setConfirmPublishOpen(false)}
      />

      <ConfirmDialog
        open={confirmClearOpen}
        title={hasCommitted ? "Clear the draft?" : "Discard new cards?"}
        message={
          hasCommitted
            ? "This removes every card from the working draft. The live carousel stays untouched until you publish again."
            : "This discards the images and details you've added but haven't saved. This can't be undone."
        }
        confirmLabel={hasCommitted ? "Clear draft" : "Discard"}
        destructive
        busy={committing}
        onConfirm={() => void doClear()}
        onCancel={() => setConfirmClearOpen(false)}
      />
    </div>
  );
};

export default HappeningNowComposeCarouselPanel;
